import type { ExtractFieldNames, FieldIndentifier, TableIdentifier } from "@packages/lib";
import { type Hotkey, useHotkey } from "@tanstack/react-hotkeys";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import type { CrmFieldProps } from "~/components/input-fields/crm-field";
import { trpc } from "~/lib/trpc";

function isEqual(a: any, b: any) {
  if (a == null && b === "") return true;
  if (b == null && a === "") return true;
  return a === b;
}

export const useCrmTableView = <T extends TableIdentifier, F extends ExtractFieldNames<T> = ExtractFieldNames<T>>(
  table: T,
  options: {
    id: number;
    /** If provided, only these fields will be fetched and made available in the view. If not provided, all fields will be fetched. */
    fields?: F[];
    /** If provided, this data will be used as the initial state of the view, and will be overwritten by the query result once it loads. This can be used to avoid loading states when we already have the data available. */
    initialData?: Record<string, unknown>;
    /** Triggered when the update is successful */
    onUpdateSuccess?: () => void;
    /** Triggered when the update fails */
    onUpdateError?: () => void;
    /**
     * Forces every field to be read-only.
     * When it's set to `true`, The tooptip shows default message
     * When it's a string, The tooptip shows the provided message
     */
    readOnly?: boolean | string;
    /** Optional hotkey for saving changes. Defaults to "Mod+S" */
    saveHotkey?: Hotkey;
    /** Optional hotkey for resetting changes. Defaults to "Mod+R" */
    resetHotkey?: Hotkey;
  },
) => {
  const qc = useQueryClient();

  const query = useQuery({
    ...trpc.genericView.get.queryOptions({
      table,
      id: options.id,
      fields: options.fields,
    }),
    initialData: options.initialData,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: Number.POSITIVE_INFINITY,
  });

  const update = useMutation(
    trpc.genericView.update.mutationOptions({
      onSuccess: async () => {
        await qc.invalidateQueries({ queryKey: trpc.genericView.get.queryKey({ table, id: options.id }) });
        options.onUpdateSuccess?.();
      },
      onError: async () => {
        options.onUpdateError?.();
      },
    }),
  );

  const [data, setData] = useState<Record<string, unknown> | undefined>();

  useEffect(() => {
    if (query.data) {
      setData(query.data);
    }
  }, [query.data]);

  const reset = () => setData(query.data);

  const dirtyFields = useMemo(() => {
    // query.data is unmodified data from db
    // data is the current state of the form, which may have unsaved changes
    if (!data || !query.data) return {};
    const dirty: Record<string, unknown> = {};
    Object.keys(query.data).forEach((key) => {
      const currentValue = data[key];
      const initialValue = query.data[key];
      if (!isEqual(currentValue, initialValue)) {
        dirty[key] = currentValue;
      }
    });
    return dirty;
  }, [query.data, data]);

  const isDirty = Object.keys(dirtyFields).length > 0;

  const getFieldProps = (fieldName: F) => {
    const fieldIdentifier = `${table}.${fieldName}` as FieldIndentifier;
    return {
      field: fieldIdentifier,
      value: data ? data[fieldName as string] : undefined,
      isDirty: Object.hasOwn(dirtyFields, fieldName),
      onChange: (value: any) => setData((prev) => ({ ...prev, [fieldName]: value })),
      readOnly: options.readOnly,
    } satisfies CrmFieldProps;
  };

  const saveChanges = async () => {
    if (!isDirty) return;
    update.mutate({
      table,
      id: options.id,
      data: dirtyFields,
    });
  };

  useHotkey(options.saveHotkey || "Mod+S", saveChanges);
  useHotkey(options.resetHotkey || "Mod+R", reset);

  return {
    isDirty,
    dirtyFields,
    getFieldProps,
    saveChanges,
    reset,
    values: data as Record<F, any> | undefined,
    isQueryPending: query.isPending,
    isQueryLoading: query.isLoading,
    isQueryFetching: query.isFetching,
    isUpdatePending: update.isPending,
  };
};
